<template>
  <div
    class="aside"
  >
    <!-- Navigation toggle -->
    <el-row
      justify="space-between"
      type="flex"
    >
      <el-col class="brand-logo">
        <img src="../../assets/logo-xs-white.png"/>
      </el-col>
      <el-col class="mobile-toggler">
        <AsideToggler/>
      </el-col>
    </el-row>

    <!-- Navigation Content -->
    <el-menu
      default-active="0"
      :defaultOpeneds="[
        '/dashboard',
      ]"
      router
      @select="closeNavigationMenuIfMobile"
    >
      <el-menu-item
        v-if="!shouldShowDetailedDashboard"
        index="/dashboard"
      >
        <i class="el-icon-menu"></i>
        <span slot="title">Dashboard</span>
      </el-menu-item>

      <el-submenu
        v-else
        ref="submenu"
        index="/dashboard"
      >
        <template slot="title">
          <i class="el-icon-menu"></i><span>Dashboard</span>
        </template>
        <el-menu-item
          v-for="(item, index) in detailedDashboardArray"
          v-if="item"
          :key="index.key"
          :index="''"
          @click="submenuClicked(item.key)"
        >
          {{ item.title }} <small>{{ item.total }}</small>
        </el-menu-item>
      </el-submenu>

      <SupportButton>
        <el-menu-item index="">
          <i class="el-icon-question"></i>
          <span slot="title">Support</span>
        </el-menu-item>
      </SupportButton>

      <a href="/#/faq/licensing" target="_blank" rel="noopener">
        <el-menu-item index="">
          <i class="el-icon-tickets" />
          <span slot="title">Licensing FAQ</span>
        </el-menu-item>
      </a>
    </el-menu>

    <!-- Navigation Footer -->
    <div class="aside__footer">
    </div>
  </div>
</template>

<script>
import SUBMISSION_STATUS from '@/assets/data/viddsee/submissionStatus';
import eventBus from '@/libs/eventBus';
import AsideToggler from './AsideToggler';
import SupportButton from '../Utilities/SupportButton';

const groupItems = (item1, item2, title, key) => {
  if ((item1 && item1.total) || (item2 && item2.total)) {
    return {
      title,
      key,
      total: (() => {
        let sum = 0;
        if (item1) sum += item1.total;
        if (item2) sum += item2.total;
        return sum;
      })(),
    };
  } else {
    return null;
  }
};

export default {
  components: {
    SupportButton,
    AsideToggler,
  },
  computed: {
    isCollapsed() {
      return this.$store.state.layout.navCollapsed;
    },
    collapseClass() {
      return (this.isCollapsed) ? 'collapsed' : '';
    },
    sidebarAction() {
      return (this.isCollapsed) ? 'Expand' : 'Collapse';
    },
    sidebarIndicatorStyle() {
      const degree = (this.isCollapsed) ? 90 : 0;
      return {
        transform: `rotate(${degree}deg)`,
      };
    },
    shouldShowDetailedDashboard() {
      return this.$route.fullPath === '/dashboard';
    },
    detailedDashboardArray() {
      const videos = this.$store.getters['submission/list'];
      const results = {};
      Object.keys(videos)
        .forEach((typeIndex) => {
          const info = SUBMISSION_STATUS.INFO[typeIndex];
          results[typeIndex] = {
            title: info.label,
            key: typeIndex,
            total: videos[typeIndex].length,
          };
        });

      // Reorganise how items are being listed here
      return [
        results[0] ? {
          title: 'Need more info',
          key: 0,
          total: results[0].length,
        } : null,
        // 2 and 3 will be combined together (under review & fasttrack)
        results[1],
        groupItems(results[2], results[3], SUBMISSION_STATUS.INFO[3].label, 3),
        groupItems(results[4], results[5], 'Reviewed', 4),
      ];
    },
  },
  methods: {
    toggleNav() {
      this.$store.commit('layout/update', {
        navCollapsed: !this.isCollapsed,
      });
    },
    submenuClicked(key) {
      eventBus.$emit('dashboard/scrollToGroup', key);
    },
    closeNavigationMenuIfMobile() {
      if (window.outerWidth < 480) {
        this.$store.commit('layout/update', {
          navCollapsed: true,
        });
      }
    },
  },
};
</script>

<style lang="scss">
$aside-bg-color: $theme-bg-color;
$aside-color: $theme-color;

// Set up width for aside
.el-aside {
  overflow: unset;
  z-index: 999; // TODO: Manage a list of z-index. element-ui's overlay uses 1000 and 2000
}

.el-aside.collapsed {
  width: 0 !important;
  position: relative;

  @include breakpoint-up(xs) {
    width: initial !important;
  }
}
.el-aside.expanded {
  min-height: 100vh;
  width: 100% !important;

  @include breakpoint-up(xs) {
    width: initial !important;
    position: relative;
  }
}

// Fix aside and menu border
.el-aside {
  background: $aside-bg-color;
  border-right: solid 1px #e6e6e6;
}
.el-menu {
  border-right: none;
}

// Set up styling to support aside__footer
.el-aside {
  position: relative;
  .aside__footer {
    position: absolute;
    bottom: 0;
  }
}

.aside {
  position: sticky;
  top: 0;
  transition: width $transition-s-xs;

  .el-menu {
    transition: margin-top $transition-s-xs;
  }

  // Setup collapsed styling
  .collapsed & {
    width: 64px;
    display: none;
    @include breakpoint-up(xs) {
      display: block;
    }

    .el-menu {
      margin-top: 0;
    }

    .el-submenu__title,
    .el-menu-item {
      span,
      .el-submenu__icon-arrow {
        opacity: 0;
      }
    }
    .el-submenu .el-menu {
      display: none;
    }
  }
  // Setup expanded styling
  .expanded & {
    width: 100%;
    @include breakpoint-up(xs) {
      width: 194px;
    }
    .el-menu {
      margin-top: 1.5em;
    }
  }

  // Add animation to opener rotation
  .aside__opener {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(50%, 50%);
    z-index: 2;
    i {
      transition: transform $transition-s-xs;
    }
  }

  // Override default styling of this aside
  .el-menu {
    background: none;
  }

  .el-submenu__title,
  .el-menu-item {
    background: none;
    opacity: 0.5;
    transition: opacity $transition-s-xs;
    color: $aside-color;

    > i,
    > span {
      color: $aside-color;
    }
  }

  .el-submenu__title,
  .el-menu-item {
    &.is-active,
    &:focus {
      background: none;
    }
    &:hover {
      background: none;
      opacity: 0.8;
    }
  }

  .el-submenu .el-menu {
    margin-top: 0;
  }
}

.mobile-toggler {
  display: block;
  @include breakpoint-up(xs) {
    display: none;
  }

  .aside__toggler {
    margin-left: auto;

    span::after,
    span::before,
    span {
      border-color: white;
    }
  }
}

// Brand
.brand-logo {
  height: 60px;
  box-sizing: border-box;
  padding: 18px 0 0 16px;
  margin-top: -2px;

  @include breakpoint-up(xs) {
    padding-right: 16px;
  }

  img {
    object-fit: cover;
    object-position: left;
    height: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Aside/index.vue